// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, Send, MessageCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

interface VideoMessage {
  id: string;
  order_id: string;
  sender_type: 'vendor' | 'customer';
  sender_name: string;
  message_text: string;
  created_at: string;
}

interface VideoOrder {
  id: string;
  vendor_id: string;
  event_id: string;
  package_id: string;
  user_id: string;
  purchaser_name: string;
  purchaser_email: string;
  player_name: string;
  total_amount_cents: number;
  status: string;
  created_at: string;
  // Joined data
  vendor?: {
    business_name: string;
    user_id: string;
  };
  event?: {
    name: string;
  };
  package?: {
    package_type: string;
  };
}

interface VideoMessageThreadProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  order: VideoOrder | null;
  userType: 'vendor' | 'customer';
}

const VideoMessageThread: React.FC<VideoMessageThreadProps> = ({
  open,
  onOpenChange,
  order,
  userType,
}) => {
  const { user, profile } = useAuth();
  const [messages, setMessages] = useState<VideoMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open && order) {
      fetchMessages();
    }
  }, [open, order]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
    }
  }, [messages]);

  const fetchMessages = async () => {
    if (!order) return;
    
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('video_messages')
        .select('*')
        .eq('order_id', order.id)
        .order('created_at', { ascending: true });

      if (error) throw error;

      setMessages(data || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error('Failed to load messages');
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !order || !user || !profile) return;

    setIsSending(true);
    try {
      // Determine sender name based on user type
      const senderName = userType === 'vendor' 
        ? order.vendor?.business_name || profile.name || 'Vendor'
        : order.purchaser_name || profile.name || 'Customer';

      console.log('Sending message:', {
        order_id: order.id,
        sender_type: userType,
        sender_name: senderName,
        user: user,
        profile: profile,
        profile_email: profile.email
      });

      const { data, error } = await supabase
        .from('video_messages')
        .insert({
          order_id: order.id,
          sender_type: userType,
          sender_name: senderName,
          message_text: newMessage.trim(),
        })
        .select()
        .single();

      if (error) {
        console.error('Insert error:', error);
        throw error;
      }

      // Add new message to local state
      setMessages(prev => [...prev, data]);
      setNewMessage('');

      // Send email and SMS notifications
      await sendEmailAndSmsNotification(data);

      toast.success('Message sent');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setIsSending(false);
    }
  };

  const sendEmailAndSmsNotification = async (message: VideoMessage) => {
    if (!order) return;

    try {
      // Determine recipient based on sender type
      const recipientEmail = userType === 'vendor' 
        ? order.purchaser_email 
        : order.vendor?.user_id; // We'd need to get vendor email from their profile

      const messageContent = `New message about your video order for ${order.event?.name}. From: ${message.sender_name}. Message: "${message.message_text}". Log in to respond.`;

      // Send email notification
      const emailData = {
        to: recipientEmail,
        subject: `New message about your video order - ${order.event?.name}`,
        message: `
          You have received a new message about your video order.
          
          Order Details:
          - Event: ${order.event?.name}
          - Package: ${order.package?.package_type}
          - From: ${message.sender_name}
          
          Message:
          "${message.message_text}"
          
          Please log in to your account to view all messages and respond.
        `,
        orderId: order.id,
      };

      // Send SMS notification
      const smsData = {
        to: order.purchaser_email, // This should be a phone number, but we'll use email for now
        message: messageContent,
      };

      // Call both edge functions simultaneously
      await Promise.all([
        invokeEdgeFunction('send-email', {
          body: emailData
        }),
        invokeEdgeFunction('send-sms', {
          body: smsData
        })
      ]);

      console.log('Email and SMS notifications sent');
    } catch (error) {
      console.error('Error sending notifications:', error);
      // Don't show error to user since message was sent successfully
    }
  };

  const formatPrice = (cents: number) => {
    return (cents / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    });
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = Math.abs(now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true 
      });
    }
  };

  if (!order) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5" />
            Order Messages
          </DialogTitle>
          <DialogDescription>
            Conversation about {order.event?.name} - {order.package?.package_type}
          </DialogDescription>
        </DialogHeader>

        {/* Order Summary */}
        <Card className="mb-4">
          <CardContent className="pt-4">
            <div className="flex justify-between items-start text-sm">
              <div>
                <p className="font-medium">{order.event?.name}</p>
                <p className="text-muted-foreground">{order.package?.package_type}</p>
                <p className="text-muted-foreground">Order #{order.id.substring(0, 8)}...</p>
              </div>
              <div className="text-right">
                <p className="font-medium">{formatPrice(order.total_amount_cents)}</p>
                <p className="text-muted-foreground capitalize">{order.status}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Messages */}
        <div className="flex-1 min-h-0">
          <ScrollArea ref={scrollAreaRef} className="h-[400px] pr-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin mr-2" />
                <span>Loading messages...</span>
              </div>
            ) : messages.length > 0 ? (
              <div className="space-y-4">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${
                      message.sender_type === userType ? 'justify-end' : 'justify-start'
                    }`}
                  >
                    <div
                      className={`max-w-[70%] rounded-lg p-3 ${
                        message.sender_type === userType
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium">{message.sender_name}</span>
                        <span className="text-xs opacity-70 flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatTimestamp(message.created_at)}
                        </span>
                      </div>
                      <p className="text-sm whitespace-pre-wrap">{message.message_text}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <MessageCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No messages yet. Start the conversation!</p>
              </div>
            )}
          </ScrollArea>
        </div>

        {/* Message Input */}
        <div className="space-y-4 pt-4 border-t">
          <Textarea
            placeholder="Type your message..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            className="min-h-[80px]"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
          />
          <div className="flex justify-between items-center">
            <p className="text-xs text-muted-foreground">
              Press Enter to send, Shift+Enter for new line
            </p>
            <Button 
              onClick={sendMessage} 
              disabled={!newMessage.trim() || isSending}
              className="flex items-center gap-2"
            >
              {isSending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              Send Message
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VideoMessageThread;